"use client";

import React from "react";
import { cn } from "@/lib/utils";
import {
  getRealmMeta,
  QB_REALM_BADGE_CLASSES,
} from "@/lib/integrations/quickbooks/realms";
import { useRealmLabels } from "@/lib/hooks/api/quickbooks/use-realm-labels";

interface RealmBadgeProps {
  /** QuickBooks realm id (tolerant of spaced/dirty input). */
  realmId: string | null | undefined;
  className?: string;
}

/**
 * Colored badge showing which QuickBooks company a row bills — West Tennessee
 * Consulting vs Schooley Mitchell (vs any future realm). Single source of the
 * realm's friendly label + color so list rows, detail headers, and other
 * surfaces stay visually consistent.
 *
 * Prefers the admin-editable label store (via useRealmLabels, one shared cached
 * request), falling back to the hardcoded registry so labels always render —
 * even while the store request is in flight or if it fails.
 *
 * Renders a muted dash when no realm is set, so callers can drop it into a cell
 * unconditionally.
 */
export function RealmBadge({ realmId, className }: RealmBadgeProps) {
  const { getEntry } = useRealmLabels();

  const fallback = getRealmMeta(realmId);
  if (!fallback) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  const override = getEntry(realmId);
  const label = override?.label ?? fallback.label;
  const badgeClasses = override
    ? QB_REALM_BADGE_CLASSES[override.category]
    : fallback.badgeClasses;

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset whitespace-nowrap",
        badgeClasses,
        className
      )}
      title={`QuickBooks: ${label}`}
    >
      {label}
    </span>
  );
}
